import * as THREE from 'three';
import { createSword, upgradeSwordModel } from '../sword/mesh';
import { createPell, disposePell, highlightZoneBand } from '../pell/mesh';
import { SessionRuntime } from './runtime';
import { GuardRuntime } from './guardRuntime';
import { ArcadeCutRuntime } from './arcadeCutRuntime';
import { ArcadeThrustRuntime } from './arcadeThrustRuntime';
import type { DrillRuntime, RuntimeSettings } from './drillRuntime';
import { SetupWizard, type SessionConfig } from '../setup/wizard';
import { PausePanel } from './pausePanel';
import { SummaryPanel } from './summary';
import { SettingsPanel, type SettingsMode, type SettingsState } from './settingsPanel';
import { clearDecals } from '../feedback/decal';
import { getSetting, setSetting } from '../settings/userSettings';
import { computePellAutoPosition } from '../ui/placement';
import { isFirebaseConfigured } from '../firebase/config';
import type { ArcadeStats, SessionStats } from './stats';
import {
  loadSwordOrientation,
  saveSwordOrientation,
  applySwordOrientation,
  type SwordOrientation,
} from '../setup/swordOrientation';
import { loadUserHeightCm, saveUserHeightCm } from '../setup/heightPanel';
import { loadDominantHand, saveDominantHand } from '../setup/handPanel';
import { loadTurnMode, saveTurnMode } from '../setup/locomotion';
import {
  loadTargetsDistance,
  loadTargetsMinSpeed,
  loadTargetsSizeFactor,
  loadTargetsSpawnDelay,
  loadTargetsTipMinT,
  loadTargetsTtl,
  saveTargetsDistance,
  saveTargetsMinSpeed,
  saveTargetsSizeFactor,
  saveTargetsSpawnDelay,
  saveTargetsTipMinT,
  saveTargetsTtl,
} from '../setup/targetsSettings';
import {
  loadPellMinArc,
  loadPellMinSpeed,
  savePellMinArc,
  savePellMinSpeed,
} from '../setup/pellSettings';
import {
  loadArcadeCutTolerance,
  loadArcadeMinSpeed,
  loadArcadeSizeFactor,
  loadArcadeSpawnDelay,
  loadArcadeTtl,
  saveArcadeCutTolerance,
  saveArcadeMinSpeed,
  saveArcadeSizeFactor,
  saveArcadeSpawnDelay,
  saveArcadeTtl,
} from '../setup/arcadeSettings';
import { loadGuardTuning, saveGuardTuning } from '../setup/guardSettings';
import { getZoneBoundaries, setZoneBoundaries } from '../setup/zoneBands';
import {
  resolveControllers,
  findInputSource,
  findInputSourceOrNull,
  findControllerForHand,
} from '../xr/controllers';
import { tickLocomotion, tickRotation, type TurnSnapState } from '../xr/locomotion';
import {
  APP_STATE,
  ARCADE_CUT_DIFFICULTY,
  ARCADE_THRUST_DIFFICULTY,
  DIFFICULTY,
  DIFFICULTY_DEFAULT,
  DRILL_TYPE,
  DURATION_MODE,
  HAND,
  TARGETS_SPAWN_DISTANCE_M,
  TECHNIQUE,
  TURN_SNAP,
  XR_BUTTON_SETTINGS_INDEX,
  type AppState,
  type ArcadeCutTuning,
  type ArcadeThrustTuning,
  type Difficulty,
  type DominantHand,
  type TurnMode,
} from '../config/constants';

export type { AppState };

export type SessionControllerDeps = {
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  session: XRSession;
  controllerA: THREE.Object3D;
  controllerB: THREE.Object3D;
  pausePanel: PausePanel;
  summaryPanel: SummaryPanel;
  settingsPanel: SettingsPanel;
};

/**
 * Owns the in-VR session lifecycle: state machine (wizard → running → paused → summary),
 * runtime/sword/pell/clear-button instances, settings-panel handlers, and per-frame ticking.
 */
export class SessionController {
  private readonly deps: SessionControllerDeps;

  private state: AppState = APP_STATE.wizard;
  private wizard: SetupWizard | null = null;
  private runtime: DrillRuntime | null = null;
  private sword: THREE.Group | null = null;
  /** Controller the sword is (or was, before sheathing) parented to. */
  private swordController: THREE.Object3D | null = null;
  /** True while the sword is parked at the user's hip for an open menu. */
  private swordSheathed = false;
  private pell: THREE.Mesh | null = null;
  private lastCfg: SessionConfig | null = null;

  private currentHand: DominantHand = loadDominantHand();
  private swordOrientation: SwordOrientation = loadSwordOrientation();
  private turnMode: TurnMode = loadTurnMode();
  private readonly turnState: TurnSnapState = { lastSnap: TURN_SNAP.neutral };

  private settingsButtonDownPrev = false;
  /** Bumped on each summary so a slow leaderboard fetch can't repaint a newer panel. */
  private rankToken = 0;
  private readonly visibilityListener: () => void;

  constructor(deps: SessionControllerDeps) {
    this.deps = deps;
    this.visibilityListener = () => this.onVisibilityChange();
    deps.session.addEventListener('visibilitychange', this.visibilityListener);
  }

  start(): void {
    void this.startSession();
  }

  /** Per-frame tick: locomotion, settings button, state-machine. */
  tickFrame(dt: number, rig: THREE.Group): void {
    if (this.state !== APP_STATE.paused) {
      tickLocomotion(this.deps.session, rig, this.deps.camera, dt);
      tickRotation(this.deps.session, rig, this.deps.camera, dt, this.turnMode, this.turnState);
    }
    this.tickSettingsButton();
    if (this.state === APP_STATE.wizard) this.wizard?.tick();
    else if (this.state === APP_STATE.running) this.runtime?.tick();
  }

  dispose(): void {
    this.deps.session.removeEventListener('visibilitychange', this.visibilityListener);
    this.wizard?.dispose();
    this.runtime?.dispose();
    this.deps.pausePanel.hide();
    this.deps.summaryPanel.hide();
    this.deps.settingsPanel.hide();
  }

  private async startSession(): Promise<void> {
    const { scene, camera, session } = this.deps;

    this.teardownActiveSession({ keepPell: false });
    this.state = APP_STATE.wizard;

    this.wizard = new SetupWizard(scene, camera, session);
    const cfg = await this.wizard.run();
    this.wizard = null;
    if (this.state !== APP_STATE.wizard) return; // session may have ended

    await this.launchSession(cfg, { reusePell: false });
  }

  /** Summary "Altra sessione" / cancel-and-retry: re-ask drill type + duration. */
  private async startNewDurationSession(): Promise<void> {
    if (!this.lastCfg) {
      void this.startSession();
      return;
    }
    const { scene, camera, session } = this.deps;
    this.teardownActiveSession({ keepPell: true });
    this.state = APP_STATE.wizard;

    this.wizard = new SetupWizard(scene, camera, session);
    const result = await this.wizard.runDurationOnly();
    this.wizard = null;
    if (this.state !== APP_STATE.wizard) return;

    // Drop the previous drill-specific fields; the new wizard result re-supplies them.
    const {
      prescriptiveTarget: _dropPresc,
      repsTarget: _dropReps,
      guardMode: _dropGuard,
      ...prevCfg
    } = this.lastCfg;
    void _dropPresc;
    void _dropReps;
    void _dropGuard;
    const nextCfg: SessionConfig = {
      ...prevCfg,
      drillType: result.drillType,
      durationMode: result.durationMode,
      durationMs: result.durationMs,
      ...(result.prescriptiveTarget ? { prescriptiveTarget: result.prescriptiveTarget } : {}),
      ...(result.repsTarget !== undefined ? { repsTarget: result.repsTarget } : {}),
      ...(result.guardMode ? { guardMode: result.guardMode } : {}),
      ...(result.technique ? { technique: result.technique } : {}),
      ...(result.difficulty ? { difficulty: result.difficulty } : {}),
    };
    await this.launchSession(nextCfg, { reusePell: nextCfg.drillType === DRILL_TYPE.pell });
  }

  /** Show the end-of-session summary with the shared action handlers. */
  private goToSummary(stats: SessionStats): void {
    this.state = APP_STATE.summary;
    const token = ++this.rankToken;
    // Practice-only runs are not persisted, ranked, or recorded as a best: free (no-timer, #44)
    // and custom-difficulty runs (#43). A preset timed run is the only competitive case.
    const competitive =
      this.lastCfg?.durationMode !== DURATION_MODE.free &&
      this.lastCfg?.difficulty !== DIFFICULTY.custom;
    this.deps.summaryPanel.show(
      stats,
      {
        onRestart: () => void this.restartCurrent(),
        onAgain: () => void this.startNewDurationSession(),
      },
      { competitive },
    );
    // Arcade ("Sfida"): submit the score and reveal the rank in the Classifica tab — competitive
    // runs only. The firebase chunk is loaded lazily so local-only mode never pulls it in.
    if (stats.kind === 'arcade' && competitive && isFirebaseConfigured()) {
      void this.submitAndShowRank(stats, token);
    }
  }

  /** Upsert the arcade score on its technique's board, fetch the rank, and hand it to the summary. */
  private async submitAndShowRank(stats: ArcadeStats, token: number): Promise<void> {
    const technique = stats.technique;
    const difficulty = stats.difficulty;
    try {
      const { submitArcadeScore, fetchLeaderboardView } = await import('../firebase/leaderboard');
      await submitArcadeScore(stats.score, stats.maxCombo, technique, difficulty);
      const view = await fetchLeaderboardView(technique, difficulty);
      if (token === this.rankToken) this.deps.summaryPanel.setLeaderboard(view);
    } catch (err) {
      console.warn('leaderboard update failed', err);
      if (token === this.rankToken) this.deps.summaryPanel.setLeaderboard(null);
    }
  }

  private async restartCurrent(): Promise<void> {
    if (!this.lastCfg) {
      void this.startSession();
      return;
    }
    this.teardownActiveSession({ keepPell: true });
    await this.launchSession(this.lastCfg, { reusePell: true });
  }

  private teardownActiveSession(opts: { keepPell: boolean }): void {
    this.deps.pausePanel.hide();
    this.deps.summaryPanel.hide();
    this.deps.settingsPanel.hide();
    clearDecals(this.deps.scene);
    if (this.runtime) this.runtime.dispose();
    this.runtime = null;
    if (this.sword?.parent) this.sword.parent.remove(this.sword);
    this.sword = null;
    this.swordController = null;
    this.swordSheathed = false;
    if (!opts.keepPell && this.pell) {
      this.deps.scene.remove(this.pell);
      disposePell(this.pell);
      this.pell = null;
    }
  }

  private async launchSession(cfg: SessionConfig, opts: { reusePell: boolean }): Promise<void> {
    const { scene, camera, session, controllerA, controllerB } = this.deps;
    this.lastCfg = cfg;
    const isGuard = cfg.drillType === DRILL_TYPE.guard;
    const isArcade = cfg.drillType === DRILL_TYPE.arcade;

    // Guard and arcade modes never use the pell mesh; pell mode reuses or rebuilds it.
    if (isGuard || isArcade) {
      if (this.pell) {
        scene.remove(this.pell);
        disposePell(this.pell);
        this.pell = null;
      }
    } else if (!opts.reusePell || !this.pell) {
      this.pell = createPell({ userHeightCm: cfg.userHeightCm });
      this.pell.position.copy(computePellAutoPosition(camera));
      scene.add(this.pell);
    }

    const { swordController, offHandController } = resolveControllers(cfg, {
      controllerA,
      controllerB,
    });

    this.sword = createSword();
    void upgradeSwordModel(this.sword);
    applySwordOrientation(this.sword, this.swordOrientation);
    swordController.add(this.sword);
    this.swordController = swordController;
    this.swordSheathed = false;
    this.currentHand = cfg.dominantHand;

    const onEnd = (stats: SessionStats): void => this.goToSummary(stats);

    if (isGuard) {
      this.runtime = new GuardRuntime({
        scene,
        camera,
        sword: this.sword,
        swordController,
        offHandController,
        userHeightCm: cfg.userHeightCm,
        dominantHand: cfg.dominantHand,
        tuning: loadGuardTuning(),
        guardMode: cfg.guardMode ?? 'all',
      });
    } else if (isArcade) {
      const difficulty = cfg.difficulty ?? DIFFICULTY_DEFAULT;
      const common = {
        scene,
        camera,
        sword: this.sword,
        swordController,
        offHandController,
        durationMs: cfg.durationMs,
        userHeightCm: cfg.userHeightCm,
        durationMode: cfg.durationMode,
        difficulty,
        onEnd,
      };
      // A preset feeds tuning from its bundle; custom reads the manual sliders (#43). Thrust
      // ("punte") reuses the former targets tunables; cut ("Sfida") keeps the arcade ones.
      if (cfg.technique === TECHNIQUE.thrust) {
        const p = this.arcadeThrustParams(difficulty);
        this.runtime = new ArcadeThrustRuntime({
          ...common,
          sizeFactor: p.sizeFactor,
          distanceM:
            difficulty === DIFFICULTY.custom ? loadTargetsDistance() : TARGETS_SPAWN_DISTANCE_M,
          ttlMs: p.ttlMs,
          spawnDelayMs: p.spawnDelayMs,
          minSpeedMPerSec: p.minSpeedMps,
          tipMinT: p.tipMinT,
        });
      } else {
        const p = this.arcadeCutParams(difficulty);
        this.runtime = new ArcadeCutRuntime({
          ...common,
          sizeFactor: p.sizeFactor,
          distanceM: TARGETS_SPAWN_DISTANCE_M,
          ttlMs: p.ttlMs,
          spawnDelayMs: p.spawnDelayMs,
          cutToleranceDeg: p.cutToleranceDeg,
          minSpeedMPerSec: p.minSpeedMps,
        });
      }
    } else {
      if (!this.pell) throw new Error('pell missing in pell-mode launchSession');
      // Prescriptive drill: fill the target zone band over the wood; bare lines otherwise.
      highlightZoneBand(this.pell, cfg.prescriptiveTarget?.zone ?? null);
      this.runtime = new SessionRuntime({
        scene,
        camera,
        pell: this.pell,
        sword: this.sword,
        swordController,
        offHandController,
        durationMs: cfg.durationMs,
        userHeightCm: cfg.userHeightCm,
        durationMode: cfg.durationMode,
        dominantHand: cfg.dominantHand,
        minStrikeSpeedMPerSec: loadPellMinSpeed(),
        minStrikeArcM: loadPellMinArc(),
        ...(cfg.prescriptiveTarget ? { prescriptiveTarget: cfg.prescriptiveTarget } : {}),
        ...(cfg.repsTarget !== undefined ? { repsTarget: cfg.repsTarget } : {}),
        onEnd,
      });
    }
    await this.runtime.init();
    this.runtime.attachInputSource(findInputSource(session, cfg.dominantHand), true);
    this.runtime.start();
    this.state = APP_STATE.running;
  }

  /** Cut tuning for a run: the manual sliders in custom, the preset bundle otherwise (#43). */
  private arcadeCutParams(difficulty: Difficulty): ArcadeCutTuning {
    if (difficulty === DIFFICULTY.custom) {
      return {
        ttlMs: loadArcadeTtl(),
        spawnDelayMs: loadArcadeSpawnDelay(),
        sizeFactor: loadArcadeSizeFactor(),
        minSpeedMps: loadArcadeMinSpeed(),
        cutToleranceDeg: loadArcadeCutTolerance(),
      };
    }
    return ARCADE_CUT_DIFFICULTY[difficulty];
  }

  /** Thrust tuning for a run: the manual sliders in custom, the preset bundle otherwise (#43). */
  private arcadeThrustParams(difficulty: Difficulty): ArcadeThrustTuning {
    if (difficulty === DIFFICULTY.custom) {
      return {
        ttlMs: loadTargetsTtl(),
        spawnDelayMs: loadTargetsSpawnDelay(),
        sizeFactor: loadTargetsSizeFactor(),
        minSpeedMps: loadTargetsMinSpeed(),
        tipMinT: loadTargetsTipMinT(),
      };
    }
    return ARCADE_THRUST_DIFFICULTY[difficulty];
  }

  /**
   * Mode-specific settings shown in the panel. On an arcade preset run the values come from the
   * bundle and are read-only (the preset owns them); custom and the other drills are editable.
   */
  private modeSettingsState(mode: SettingsMode): {
    fields: Partial<SettingsState>;
    readOnly: boolean;
  } {
    const difficulty = this.lastCfg?.difficulty ?? DIFFICULTY_DEFAULT;
    if (mode === 'targets') {
      if (difficulty === DIFFICULTY.custom) {
        return {
          readOnly: false,
          fields: {
            targetsSizeFactor: loadTargetsSizeFactor(),
            targetsMinSpeed: loadTargetsMinSpeed(),
            targetsDistanceM: loadTargetsDistance(),
            targetsTtlMs: loadTargetsTtl(),
            targetsSpawnDelayMs: loadTargetsSpawnDelay(),
            targetsTipMinT: loadTargetsTipMinT(),
          },
        };
      }
      const p = ARCADE_THRUST_DIFFICULTY[difficulty];
      return {
        readOnly: true,
        fields: {
          targetsSizeFactor: p.sizeFactor,
          targetsMinSpeed: p.minSpeedMps,
          targetsTtlMs: p.ttlMs,
          targetsSpawnDelayMs: p.spawnDelayMs,
          targetsTipMinT: p.tipMinT,
        },
      };
    }
    if (mode === 'arcade') {
      if (difficulty === DIFFICULTY.custom) {
        return {
          readOnly: false,
          fields: {
            arcadeTtlMs: loadArcadeTtl(),
            arcadeSpawnDelayMs: loadArcadeSpawnDelay(),
            arcadeCutToleranceDeg: loadArcadeCutTolerance(),
            arcadeSizeFactor: loadArcadeSizeFactor(),
            arcadeMinSpeed: loadArcadeMinSpeed(),
          },
        };
      }
      const p = ARCADE_CUT_DIFFICULTY[difficulty];
      return {
        readOnly: true,
        fields: {
          arcadeTtlMs: p.ttlMs,
          arcadeSpawnDelayMs: p.spawnDelayMs,
          arcadeCutToleranceDeg: p.cutToleranceDeg,
          arcadeSizeFactor: p.sizeFactor,
          arcadeMinSpeed: p.minSpeedMps,
        },
      };
    }
    if (mode === 'guard') return { readOnly: false, fields: { guardTuning: loadGuardTuning() } };
    return {
      readOnly: false,
      fields: {
        zoneBoundaries: getZoneBoundaries(),
        pellMinSpeed: loadPellMinSpeed(),
        pellMinArc: loadPellMinArc(),
      },
    };
  }

  private applyHeightChange(cm: number): void {
    saveUserHeightCm(cm);
    if (!this.runtime) return;
    this.rebuildPell(cm);
    this.runtime.setUserHeightCm(cm);
  }

  /** Apply edited body-zone bands: persist + rebuild the pell so its zone overlay matches. */
  private applyZoneBoundaries(next: number[]): void {
    const cur = getZoneBoundaries();
    if (next.length === cur.length && next.every((v, i) => v === cur[i])) return;
    setZoneBoundaries(next);
    this.rebuildPell(loadUserHeightCm());
  }

  /** Rebuild the pell mesh in place (height baked at creation; zone overlay redraws live). Pell mode only. */
  private rebuildPell(userHeightCm: number): void {
    if (!(this.runtime instanceof SessionRuntime) || !this.pell) return;
    const oldPellPos = this.pell.position.clone();
    this.deps.scene.remove(this.pell);
    disposePell(this.pell);
    this.pell = createPell({ userHeightCm });
    this.pell.position.copy(oldPellPos);
    this.deps.scene.add(this.pell);
    this.runtime.setPell(this.pell);
  }

  private applyOrientation(o: SwordOrientation): void {
    this.swordOrientation = o;
    saveSwordOrientation(o);
    if (this.sword && !this.swordSheathed) applySwordOrientation(this.sword, o);
  }

  /**
   * Hide the sword while a menu is open, so the blade doesn't hang in the way.
   * It used to be parked at the hip (re-parented to the camera), but that head-locked
   * pose is inconsistent on the headset (the blade stays drawn there while it tucks
   * away in the desktop emulator). Hiding it is identical on both platforms.
   */
  private sheatheSword(): void {
    if (!this.sword || this.swordSheathed) return;
    this.swordSheathed = true;
    this.sword.visible = false;
  }

  /** Re-show the sword on its controller and restore the held orientation. */
  private drawSword(): void {
    if (!this.sword || !this.swordSheathed || !this.swordController) return;
    this.swordSheathed = false;
    this.sword.visible = true;
    if (this.sword.parent !== this.swordController) {
      if (this.sword.parent) this.sword.parent.remove(this.sword);
      this.swordController.add(this.sword);
    }
    applySwordOrientation(this.sword, this.swordOrientation);
  }

  /**
   * Re-resolve which controller object tracks the dominant hand and re-parent the
   * sword accordingly. The Quest browser can reorder `session.inputSources` across a
   * Meta-button suspend/resume, which would otherwise leave the sword (and the
   * runtime's notion of "sword controller") attached to the wrong hand.
   */
  private reresolveControllers(): void {
    if (!this.sword || !this.runtime || !this.lastCfg) return;
    const { session, controllerA, controllerB } = this.deps;
    const { swordController, offHandController } = resolveControllers(this.lastCfg, {
      controllerA,
      controllerB,
    });
    this.swordController = swordController;
    const newInputSource = findInputSourceOrNull(session, this.currentHand);
    this.runtime.setControllers(swordController, offHandController, newInputSource);
  }

  private applyHandChange(hand: DominantHand): void {
    if (hand === this.currentHand) return;
    this.currentHand = hand;
    saveDominantHand(hand);
    if (!this.sword || !this.runtime) return;

    const { session, controllerA, controllerB } = this.deps;
    const newSwordController = findControllerForHand(hand, controllerA, controllerB);
    const newOffHandController = newSwordController === controllerA ? controllerB : controllerA;

    // While sheathed (a menu is open) the sword is parked at the hip, not on a
    // controller; just remember the new controller for when the menu closes.
    if (!this.swordSheathed) {
      if (this.sword.parent) this.sword.parent.remove(this.sword);
      newSwordController.add(this.sword);
    }
    this.swordController = newSwordController;

    const newInputSource = findInputSourceOrNull(session, hand);
    this.runtime.setControllers(newSwordController, newOffHandController, newInputSource);
    this.runtime.applySettings({ dominantHand: hand });
  }

  private toggleSettings(): void {
    if (this.deps.settingsPanel.isVisible()) {
      this.closeSettings();
      return;
    }
    this.openSettings();
  }

  /** Open the settings panel. Pauses the runtime (Q: opening settings stops the clock). */
  private openSettings(): void {
    const { settingsPanel } = this.deps;
    this.runtime?.pause();
    this.sheatheSword();

    // Arcade hosts two techniques: thrust reuses the former targets sliders, cut keeps the
    // arcade ones — so the panel's mode is driven by (drillType, technique), not drillType alone.
    const dt = this.runtime?.drillType;
    const mode: SettingsMode =
      dt === DRILL_TYPE.guard
        ? 'guard'
        : dt === DRILL_TYPE.arcade
          ? this.lastCfg?.technique === TECHNIQUE.thrust
            ? 'targets'
            : 'arcade'
          : 'pell';

    const modeState = this.modeSettingsState(mode);
    settingsPanel.show(
      {
        heightCm: loadUserHeightCm(),
        pitchDeg: this.swordOrientation.pitchDeg,
        yawDeg: this.swordOrientation.yawDeg,
        rollDeg: this.swordOrientation.rollDeg,
        dominantHand: this.currentHand,
        turnMode: this.turnMode,
        mode,
        modeReadOnly: modeState.readOnly,
        sfxVolume: getSetting('sfxVolume'),
        musicVolume: getSetting('musicVolume'),
        ...modeState.fields,
      },
      {
        onChange: (next) => this.applySettingsChange(next),
        onClose: () => this.closeSettings(),
        onRestart: () => void this.restartCurrent(),
        onCancelSession: () => {
          settingsPanel.hide();
          if (this.runtime) this.goToSummary(this.runtime.end());
        },
      },
    );
  }

  /** Hide the settings panel and resume the runtime. */
  private closeSettings(): void {
    this.deps.settingsPanel.hide();
    this.drawSword();
    this.runtime?.resume();
  }

  private applySettingsChange(next: SettingsState): void {
    if (next.heightCm !== loadUserHeightCm()) this.applyHeightChange(next.heightCm);
    if (
      next.pitchDeg !== this.swordOrientation.pitchDeg ||
      next.yawDeg !== this.swordOrientation.yawDeg ||
      next.rollDeg !== this.swordOrientation.rollDeg
    ) {
      this.applyOrientation({
        pitchDeg: next.pitchDeg,
        yawDeg: next.yawDeg,
        rollDeg: next.rollDeg,
      });
    }
    if (next.dominantHand !== this.currentHand) this.applyHandChange(next.dominantHand);
    if (next.turnMode !== this.turnMode) {
      this.turnMode = next.turnMode;
      saveTurnMode(this.turnMode);
      this.turnState.lastSnap = TURN_SNAP.neutral;
    }
    // Mode-specific tunables: persist each changed value, then hand the batch to the
    // runtime — it applies the keys it understands and ignores the rest.
    const runtimeSettings: Partial<RuntimeSettings> = {};
    // On an arcade preset run the mode sliders are read-only — the panel echoes the bundle
    // values back on every general/audio change, so persisting them here would corrupt the
    // custom sliders and retune the live preset. Skip them unless the run is custom (#43).
    const arcadePreset =
      this.lastCfg?.drillType === DRILL_TYPE.arcade &&
      (this.lastCfg?.difficulty ?? DIFFICULTY_DEFAULT) !== DIFFICULTY.custom;
    if (!arcadePreset) {
      if (
        next.targetsSizeFactor !== undefined &&
        next.targetsSizeFactor !== loadTargetsSizeFactor()
      ) {
        saveTargetsSizeFactor(next.targetsSizeFactor);
        runtimeSettings.targetsSizeFactor = next.targetsSizeFactor;
      }
      if (next.targetsMinSpeed !== undefined && next.targetsMinSpeed !== loadTargetsMinSpeed()) {
        saveTargetsMinSpeed(next.targetsMinSpeed);
        runtimeSettings.targetsMinSpeed = next.targetsMinSpeed;
      }
      if (next.targetsDistanceM !== undefined && next.targetsDistanceM !== loadTargetsDistance()) {
        saveTargetsDistance(next.targetsDistanceM);
        runtimeSettings.targetsDistanceM = next.targetsDistanceM;
      }
      if (next.targetsTtlMs !== undefined && next.targetsTtlMs !== loadTargetsTtl()) {
        saveTargetsTtl(next.targetsTtlMs);
        runtimeSettings.targetsTtlMs = next.targetsTtlMs;
      }
      if (
        next.targetsSpawnDelayMs !== undefined &&
        next.targetsSpawnDelayMs !== loadTargetsSpawnDelay()
      ) {
        saveTargetsSpawnDelay(next.targetsSpawnDelayMs);
        runtimeSettings.targetsSpawnDelayMs = next.targetsSpawnDelayMs;
      }
      if (next.targetsTipMinT !== undefined && next.targetsTipMinT !== loadTargetsTipMinT()) {
        saveTargetsTipMinT(next.targetsTipMinT);
        runtimeSettings.targetsTipMinT = next.targetsTipMinT;
      }
      if (next.arcadeTtlMs !== undefined && next.arcadeTtlMs !== loadArcadeTtl()) {
        saveArcadeTtl(next.arcadeTtlMs);
        runtimeSettings.arcadeTtlMs = next.arcadeTtlMs;
      }
      if (
        next.arcadeSpawnDelayMs !== undefined &&
        next.arcadeSpawnDelayMs !== loadArcadeSpawnDelay()
      ) {
        saveArcadeSpawnDelay(next.arcadeSpawnDelayMs);
        runtimeSettings.arcadeSpawnDelayMs = next.arcadeSpawnDelayMs;
      }
      if (
        next.arcadeCutToleranceDeg !== undefined &&
        next.arcadeCutToleranceDeg !== loadArcadeCutTolerance()
      ) {
        saveArcadeCutTolerance(next.arcadeCutToleranceDeg);
        runtimeSettings.arcadeCutToleranceDeg = next.arcadeCutToleranceDeg;
      }
      if (next.arcadeSizeFactor !== undefined && next.arcadeSizeFactor !== loadArcadeSizeFactor()) {
        saveArcadeSizeFactor(next.arcadeSizeFactor);
        runtimeSettings.arcadeSizeFactor = next.arcadeSizeFactor;
      }
      if (next.arcadeMinSpeed !== undefined && next.arcadeMinSpeed !== loadArcadeMinSpeed()) {
        saveArcadeMinSpeed(next.arcadeMinSpeed);
        runtimeSettings.arcadeMinSpeed = next.arcadeMinSpeed;
      }
    }
    if (next.pellMinSpeed !== undefined && next.pellMinSpeed !== loadPellMinSpeed()) {
      savePellMinSpeed(next.pellMinSpeed);
      runtimeSettings.pellMinSpeed = next.pellMinSpeed;
    }
    if (next.pellMinArc !== undefined && next.pellMinArc !== loadPellMinArc()) {
      savePellMinArc(next.pellMinArc);
      runtimeSettings.pellMinArc = next.pellMinArc;
    }
    if (next.guardTuning) {
      saveGuardTuning(next.guardTuning);
      runtimeSettings.guardTuning = next.guardTuning;
    }
    if (Object.keys(runtimeSettings).length > 0) this.runtime?.applySettings(runtimeSettings);
    if (next.zoneBoundaries) this.applyZoneBoundaries(next.zoneBoundaries);
    // Volumes: persist on change; the shared AudioManager reads them live via its subscription.
    if (next.sfxVolume !== getSetting('sfxVolume')) setSetting('sfxVolume', next.sfxVolume);
    if (next.musicVolume !== getSetting('musicVolume')) setSetting('musicVolume', next.musicVolume);
  }

  /** Rising-edge detection on the left controller's settings button. */
  private tickSettingsButton(): void {
    if (this.state !== APP_STATE.running) return;
    let pressed = false;
    for (const src of this.deps.session.inputSources) {
      if (src.handedness !== HAND.left) continue;
      const gp = src.gamepad;
      if (!gp) break;
      pressed = gp.buttons[XR_BUTTON_SETTINGS_INDEX]?.pressed ?? false;
      break;
    }
    if (pressed && !this.settingsButtonDownPrev) this.toggleSettings();
    this.settingsButtonDownPrev = pressed;
  }

  private onVisibilityChange(): void {
    const { session } = this.deps;
    if (this.state === APP_STATE.running && session.visibilityState !== 'visible') {
      this.runtime?.pause();
      this.state = APP_STATE.paused;
      this.sheatheSword();
      this.deps.pausePanel.show({
        onResume: () => {
          this.reresolveControllers();
          this.drawSword();
          this.runtime?.resume();
          this.state = APP_STATE.running;
        },
        onEnd: () => {
          if (!this.runtime) return;
          this.goToSummary(this.runtime.end());
        },
      });
    }
    // visible → paused: leave panel up; explicit resume click required.
  }
}
